import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '../types'
import { authService } from '../services/authService'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoggingIn: boolean // Flag para prevenir login duplo
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoggingIn: false,
      login: async (email: string, password: string) => {
        // Previne múltiplas chamadas simultâneas
        if (get().isLoggingIn) {
          console.warn('⚠️ Login já em andamento, ignorando nova tentativa')
          return
        }

        console.log('📞 authStore.login() chamado com:', email)
        set({ isLoggingIn: true })
        
        try {
          const response = await authService.login({ email, password })
          console.log('📦 Resposta recebida do authService:', response)
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoggingIn: false,
          })
          console.log('✅ Estado do Zustand atualizado')
        } catch (error) {
          console.error('❌ Erro no authStore.login:', error)
          set({ isLoggingIn: false })
          throw error
        }
      },
      logout: async () => {
        console.log('🚪 Iniciando logout...')
        
        // Guarda o token antes de limpar (necessário para a requisição HTTP)
        const currentToken = get().token
        
        if (currentToken) {
          try {
            // Faz a requisição de logout com o token ainda válido
            await authService.logout()
            console.log('✅ Logout no backend bem-sucedido')
          } catch (error) {
            console.error('❌ Erro ao fazer logout na API:', error)
            // Continua com logout local mesmo se a API falhar
          }
        }
        
        // Limpa o estado local (sempre executa, mesmo se a API falhar)
        set({ user: null, token: null, isAuthenticated: false, isLoggingIn: false })
        console.log('✅ Estado local limpo')
      },
      setUser: (user: User) => {
        set({ user, token: user.token, isAuthenticated: true })
      },
    }),
    {
      name: 'auth-storage',
    }
  )
)
